import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/auth/auth-provider";
import { useAuth } from "./hooks/use-auth";
import { type JSX } from "react";
import { ToastProvider } from "./contexts/toast/toast-context";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Login from "./pages/login";
import Conversas from "./pages/painel/conversas";
import Configuracoes from "./pages/painel/config";
import ListTenants from "./pages/painel/tenant";
import CadastroTenant from "./pages/painel/tenant/cadastro-tenant";
import ListSystemUser from "./pages/painel/system-user";
import AuthenticatedLayout from "./layouts/authenticated-layout";
import CadastroSystemUser from "./pages/painel/system-user/cadastro-system-user";
import ConversationsReport from "./pages/painel/report";
import Dashboard from "./pages/painel/dashboard";
import DashboardFunil from "./pages/painel/dashboard-funil";
import ChangePassword from "./pages/painel/password";
import { ResetPassword } from "./pages/reset-password";
import { HomePage } from "./pages/Home";
import BlockedContacts from "./pages/painel/config/blocked-contacts";
import PredefinedMessagesPage from "./pages/painel/config/predefined-messages";
import WeeklyScheduleConfig from "./pages/painel/config/weekly-schedule";
import AttendanceWindowConfig from "./pages/painel/config/attendance-window";
import TenantAiConfig from "./pages/painel/config/ai-tenant";
import AiSkillsPage from "./pages/painel/config/ai-skills";
import CadastroAiSkill from "./pages/painel/config/ai-skills/cadastro-ai-skill";
import AiModelsPage from "./pages/painel/config/ai-models";
import TenantRagConfig from "./pages/painel/config/rag-tenant";
import ListClientes from "./pages/painel/clientes";
import CadastroCliente from "./pages/painel/clientes/cadastro-cliente";
import VisualizarCliente from "./pages/painel/clientes/visualizar-cliente";
import AppointmentPage from "./pages/painel/appointment";
import LoggedAttendantsPage from "./pages/painel/config/logged-attendants";
import TenantChargePage from "./pages/painel/config/tenant-charge";
import EstablishmentLocationConfig from "./pages/painel/config/establishment-location";
import AppointmentReminderConfigPage from "./pages/painel/config/appointment-reminders";
import TrainingVideosPage from "./pages/painel/config/training-videos";
import NotificationsPage from "./pages/painel/config/notifications";
import { ProtectedResourceRoute } from "./components/auth/protected-resource-route";
import { APP_RESOURCES, type AppResource } from "./permissions/access-control";

import ProtocolsPage from "./pages/painel/protocols";
import CreateProtocolPage from "./pages/painel/protocols/create";
import ProtocolDetailPage from "./pages/painel/protocols/detail";
import { TenantFeaturesProvider } from "./contexts/tenant-features";
import CommercialFunnelPage from "./pages/painel/commercial-funnel";
import PrivacyPolicyPage from "./pages/privacy-policy";
import CampaignsPage from "./pages/painel/campaigns";
import CampaignWizardPage from "./pages/painel/campaigns/wizard";
import CampaignDetailPage from "./pages/painel/campaigns/detail";

function PrivateRoute({ children }: { children: JSX.Element }) {
  const { user, isAuthReady } = useAuth();

  if (!isAuthReady) {
    return null;
  }

  return user ? children : <Navigate to="/login" />;
}

function LegacyClientesRedirect() {
  const location = useLocation();

  return (
    <Navigate
      replace
      to={{
        pathname: location.pathname.replace(
          "/painel/clientes",
          "/painel/contatos",
        ),
        search: location.search,
        hash: location.hash,
      }}
    />
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const protect = (
    resource: AppResource,
    element: JSX.Element,
    title: string,
  ) => (
    <ProtectedResourceRoute resource={resource} title={title}>
      {element}
    </ProtectedResourceRoute>
  );

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route
        path="/painel"
        element={
          <PrivateRoute>
            <AuthenticatedLayout />
          </PrivateRoute>
        }
      >
        <Route
          index
          element={protect(
            APP_RESOURCES.PANEL_CONVERSATIONS,
            <Conversas />,
            "Atendimentos",
          )}
        />
        <Route
          path="dashboard"
          element={protect(
            APP_RESOURCES.PANEL_DASHBOARD,
            <Dashboard />,
            "Dashboard",
          )}
        />
        <Route
          path="dashboard-funil"
          element={protect(
            APP_RESOURCES.PANEL_DASHBOARD_FUNNEL,
            <DashboardFunil />,
            "Esteira de Atendimento",
          )}
        />
        <Route
          path="conversas"
          element={protect(
            APP_RESOURCES.PANEL_CONVERSATIONS,
            <Conversas />,
            "Atendimentos",
          )}
        />
        <Route
          path="report"
          element={protect(
            APP_RESOURCES.PANEL_REPORT,
            <ConversationsReport />,
            "Relatórios",
          )}
        />
        <Route
          path="contatos"
          element={protect(
            APP_RESOURCES.PANEL_CLIENTS,
            <ListClientes />,
            "Contatos",
          )}
        />
        <Route
          path="comercial/funil"
          element={protect(
            APP_RESOURCES.PANEL_COMMERCIAL_FUNNEL,
            <CommercialFunnelPage />,
            "Funil de vendas",
          )}
        />
        <Route
          path="comercial/campanhas"
          element={protect(
            APP_RESOURCES.PANEL_COMMERCIAL_CAMPAIGNS,
            <CampaignsPage />,
            "Campanhas",
          )}
        />
        <Route
          path="comercial/campanhas/nova"
          element={protect(
            APP_RESOURCES.PANEL_COMMERCIAL_CAMPAIGNS,
            <CampaignWizardPage />,
            "Nova campanha",
          )}
        />
        <Route
          path="comercial/campanhas/:id"
          element={protect(
            APP_RESOURCES.PANEL_COMMERCIAL_CAMPAIGNS,
            <CampaignDetailPage />,
            "Campanha",
          )}
        />
        <Route
          path="contatos/cadastro"
          element={protect(
            APP_RESOURCES.PANEL_CLIENTS,
            <CadastroCliente />,
            "Contatos",
          )}
        />
        <Route
          path="contatos/editar/:id"
          element={protect(
            APP_RESOURCES.PANEL_CLIENTS,
            <CadastroCliente />,
            "Contatos",
          )}
        />
        <Route
          path="contatos/visualizar/:id"
          element={protect(
            APP_RESOURCES.PANEL_CLIENTS,
            <VisualizarCliente />,
            "Contatos",
          )}
        />
        <Route path="clientes/*" element={<LegacyClientesRedirect />} />
        <Route
          path="protocolos"
          element={protect(
            APP_RESOURCES.PANEL_PROTOCOLS,
            <ProtocolsPage />,
            "Protocolos",
          )}
        />
        <Route
          path="protocolos/novo"
          element={protect(
            APP_RESOURCES.PANEL_PROTOCOLS,
            <CreateProtocolPage />,
            "Novo protocolo",
          )}
        />
        <Route
          path="protocolos/:id"
          element={protect(
            APP_RESOURCES.PANEL_PROTOCOLS,
            <ProtocolDetailPage />,
            "Protocolo",
          )}
        />
        <Route
          path="appointment"
          element={protect(
            APP_RESOURCES.PANEL_APPOINTMENTS,
            <AppointmentPage />,
            "Agendamentos",
          )}
        />
        <Route
          path="config"
          element={protect(
            APP_RESOURCES.PANEL_CONFIG,
            <Configuracoes />,
            "Configurações",
          )}
        />
        <Route
          path="config/tenant"
          element={protect(
            APP_RESOURCES.CONFIG_TENANT,
            <ListTenants />,
            "Tenants",
          )}
        />
        <Route
          path="config/tenant/cadastro"
          element={protect(
            APP_RESOURCES.CONFIG_TENANT,
            <CadastroTenant />,
            "Novo Tenant",
          )}
        />
        <Route
          path="config/tenant/editar/:id"
          element={protect(
            APP_RESOURCES.CONFIG_TENANT,
            <CadastroTenant />,
            "Editar Tenant",
          )}
        />
        <Route
          path="config/establishment-location"
          element={protect(
            APP_RESOURCES.CONFIG_ESTABLISHMENT_LOCATION,
            <EstablishmentLocationConfig />,
            "Localização do estabelecimento",
          )}
        />
        <Route
          path="config/system-user"
          element={protect(
            APP_RESOURCES.CONFIG_SYSTEM_USER,
            <ListSystemUser />,
            "Usuários do sistema",
          )}
        />
        <Route
          path="config/system-user/cadastro"
          element={protect(
            APP_RESOURCES.CONFIG_SYSTEM_USER,
            <CadastroSystemUser />,
            "Novo Usuário do Sistema",
          )}
        />
        <Route
          path="config/system-user/editar/:id"
          element={protect(
            APP_RESOURCES.CONFIG_SYSTEM_USER,
            <CadastroSystemUser />,
            "Editar Usuário do Sistema",
          )}
        />
        <Route
          path="config/blocked-contacts"
          element={protect(
            APP_RESOURCES.CONFIG_BLOCKED_CONTACTS,
            <BlockedContacts />,
            "Contatos bloqueados",
          )}
        />
        <Route
          path="config/predefined-messages"
          element={protect(
            APP_RESOURCES.CONFIG_PREDEFINED_MESSAGES,
            <PredefinedMessagesPage />,
            "Mensagens pré-definidas",
          )}
        />
        <Route
          path="config/logged-attendants"
          element={protect(
            APP_RESOURCES.CONFIG_LOGGED_ATTENDANTS,
            <LoggedAttendantsPage />,
            "Atendentes logados",
          )}
        />
        <Route
          path="config/tenant-charge"
          element={protect(
            APP_RESOURCES.CONFIG_TENANT_CHARGE,
            <TenantChargePage />,
            "Custos por tenant",
          )}
        />
        <Route
          path="config/attendance-window"
          element={protect(
            APP_RESOURCES.CONFIG_ATTENDANCE_WINDOW,
            <AttendanceWindowConfig />,
            "Janela de atendimento",
          )}
        />
        <Route
          path="config/weekly-schedule"
          element={protect(
            APP_RESOURCES.CONFIG_WEEKLY_SCHEDULE,
            <WeeklyScheduleConfig />,
            "Agenda semanal",
          )}
        />
        <Route
          path="config/appointment-settings"
          element={protect(
            APP_RESOURCES.CONFIG_APPOINTMENT_REMINDERS,
            <AppointmentReminderConfigPage />,
            "Configurações de agendamento",
          )}
        />
        <Route
          path="config/appointment-reminders"
          element={
            <Navigate replace to="/painel/config/appointment-settings" />
          }
        />
        <Route
          path="config/ai-tenant"
          element={protect(
            APP_RESOURCES.CONFIG_AI_TENANT,
            <TenantAiConfig />,
            "Configurações de IA",
          )}
        />
        <Route
          path="config/ai-skills"
          element={protect(
            APP_RESOURCES.CONFIG_AI_SKILLS,
            <AiSkillsPage />,
            "Skills de IA",
          )}
        />
        <Route
          path="config/ai-models"
          element={protect(
            APP_RESOURCES.CONFIG_AI_MODELS,
            <AiModelsPage />,
            "Modelos de IA",
          )}
        />
        <Route
          path="config/ai-skills/cadastro"
          element={protect(
            APP_RESOURCES.CONFIG_AI_SKILLS,
            <CadastroAiSkill />,
            "Nova Skill de IA",
          )}
        />
        <Route
          path="config/ai-skills/editar/:id"
          element={protect(
            APP_RESOURCES.CONFIG_AI_SKILLS,
            <CadastroAiSkill />,
            "Editar Skill de IA",
          )}
        />
        <Route
          path="config/rag-tenant"
          element={protect(
            APP_RESOURCES.CONFIG_RAG_TENANT,
            <TenantRagConfig />,
            "Configurações de RAG",
          )}
        />
        <Route
          path="config/training-videos"
          element={protect(
            APP_RESOURCES.CONFIG_TRAINING_VIDEOS,
            <TrainingVideosPage />,
            "Treinamentos",
          )}
        />
        <Route
          path="config/notifications"
          element={protect(
            APP_RESOURCES.CONFIG_NOTIFICATIONS,
            <NotificationsPage />,
            "Notificações",
          )}
        />
        <Route
          path="config/password-change"
          element={protect(
            APP_RESOURCES.CONFIG_PASSWORD_CHANGE,
            <ChangePassword />,
            "Alterar senha",
          )}
        />

        <Route path="*" element={<Navigate to="/painel/conversas" />} />
      </Route>

      <Route
        path="*"
        element={<Navigate to={user ? "/painel/conversas" : "/login"} />}
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <TenantFeaturesProvider>
          <BrowserRouter>
            <AppRoutes />
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              pauseOnHover
              draggable
              theme="colored"
            />
          </BrowserRouter>
        </TenantFeaturesProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
